import { useMemo } from 'react'
import { useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { useParams } from 'wouter'
import { useLingui } from '@lingui/react/macro'

import { CategoryForm } from '@/features/layout'
import { Suspense } from '@/components/ui/Suspense'
import { DialogHeader } from '@/layouts/Dialog'
import { useCloseModal } from '@/Router'

export function Category({
  layoutVersion,
  slug
}: {
  layoutVersion: number
  slug: string
}) {
  const { t } = useLingui()
  const closeModal = useCloseModal()

  const { data } = useSuspenseQuery(AdminAPI.layout.GET_BY_VERSION, {
    variables: {
      version: layoutVersion
    },
    fetchPolicy: 'cache-and-network'
  })

  const layout = useMemo(
    () =>
      getFragment(SharedAPI.layout.LAYOUT_FRAGMENT, data.getLayoutByVersion),
    [data]
  )

  const { data: categoryData } = useSuspenseQuery(
    AdminAPI.category.GET_BY_LAYOUT_AND_SLUG,
    {
      variables: {
        layoutId: layout.id,
        slug
      }
    }
  )

  const category = useMemo(() => {
    return getFragment(
      SharedAPI.category.CATEGORY_FRAGMENT,
      categoryData?.getCategoryByLayoutAndSlug
    )
  }, [categoryData])

  if (!category) return null

  const name = category.name

  return (
    <div className="flex flex-col gap-7 p-5">
      <DialogHeader
        title={t`Category`}
        description={t({
          comment: 'Description for the Category Dialog',
          message: `Show/Edit the category "${name}"`
        })}
      />
      <CategoryForm
        category={category}
        onCancel={closeModal}
        onDone={closeModal}
      />
    </div>
  )
}

export default function CategorySuspense() {
  const params = useParams() as { layoutVersion: string; slug: string }

  if (!params) return null

  return (
    <Suspense isDialog>
      <Category
        slug={params.slug}
        layoutVersion={parseInt(params.layoutVersion)}
      />
    </Suspense>
  )
}
